import React, { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { api } from "@/lib/api";
import { MapPin, RotateCcw } from "lucide-react";
import roTopo from "@/lib/romania_counties.topo.json";

/**
 * Real SVG interactive map of Romania (42 counties + București).
 * Click a county → onSelect(county name). Color intensity reflects institution density.
 * Data source: topojson from brechtv/looker_map_layers (public domain).
 */

// Normalize the county name coming from the topojson to our canonical Romanian
const normalizeName = (raw) => {
  if (!raw) return "";
  if (raw === "Bucharest") return "București";
  return raw;
};

const colorForRatio = (ratio) => {
  // 0 = cold, 1 = hot. Gold ramp with warm accents.
  if (ratio === 0) return "#1B2543";
  if (ratio < 0.2) return "#3A2E1E";
  if (ratio < 0.4) return "#6A4E28";
  if (ratio < 0.6) return "#A0762E";
  if (ratio < 0.8) return "#C8952E";
  return "#D4A63D";
};

export const RomaniaMap = ({ selected, onSelect }) => {
  const [rows, setRows] = useState([]);
  const [tooltip, setTooltip] = useState(null); // {name, count, x, y}
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Aggregate institution counts by county from server
    api.get("/institutions/coverage")
      .then(r => setRows(Object.entries(r.data?.counts || {}).map(([c, n]) => ({ county: c, count: n }))))
      .catch(() => {});
  }, []);

  const { maxCount, byCounty, total } = useMemo(() => {
    const map = {};
    let mx = 0;
    let t = 0;
    for (const r of rows) {
      map[r.county] = r.count;
      if (r.count > mx) mx = r.count;
      t += r.count;
    }
    return { maxCount: mx || 1, byCounty: map, total: t };
  }, [rows]);

  const handleMouseMove = (e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  return (
    <div
      className="rounded-md border border-white/10 bg-gradient-to-br from-[#0F1830] via-[#12193A] to-[#0A1020] p-5"
      data-testid="romania-map"
      onMouseMove={handleMouseMove}
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#A0AABF] font-bold flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5 text-[#D4A63D]" /> Harta interactivă a României
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-[#A0AABF]">
            <b className="text-white">{total}</b> instituții · <b className="text-white">{Object.keys(byCounty).length}</b> județe
          </span>
          {selected && (
            <button
              onClick={() => onSelect(null)}
              data-testid="map-reset"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border border-[#E89BB8]/40 text-[#E89BB8] hover:bg-[#E89BB8]/10 uppercase tracking-wider"
            >
              <RotateCcw className="w-3 h-3" /> Resetare
            </button>
          )}
        </div>
      </div>

      <div className="relative" style={{ aspectRatio: "16 / 10" }}>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ scale: 3200, center: [25, 45.9] }}
          width={900}
          height={560}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup zoom={1} minZoom={1} maxZoom={4}>
            <Geographies geography={roTopo}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const rawName = geo.properties.NAME_1;
                  const name = normalizeName(rawName);
                  const count = byCounty[name] || 0;
                  const ratio = count / maxCount;
                  const isSelected = selected === name;
                  const baseFill = colorForRatio(ratio);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      data-testid={`map-county-${name}`}
                      onClick={() => onSelect && onSelect(name)}
                      onMouseEnter={() => setTooltip({ name, count })}
                      onMouseLeave={() => setTooltip(null)}
                      style={{
                        default: {
                          fill: isSelected ? "#5FA0E8" : baseFill,
                          stroke: isSelected ? "#8FBFF0" : "#0A1020",
                          strokeWidth: isSelected ? 1.6 : 0.5,
                          outline: "none",
                          transition: "all 0.2s",
                        },
                        hover: {
                          fill: isSelected ? "#8FBFF0" : "#E89BB8",
                          stroke: isSelected ? "#8FBFF0" : "#F2C053",
                          strokeWidth: 1.2,
                          outline: "none",
                          cursor: "pointer",
                        },
                        pressed: { fill: "#5FA0E8", outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {/* Tooltip */}
        {tooltip && (
          <div
            className="pointer-events-none fixed z-50 px-3 py-1.5 rounded-md bg-[#0A1020] border border-[#D4A63D]/50 text-xs shadow-xl"
            style={{ left: mousePos.x + 14, top: mousePos.y + 14 }}
          >
            <div className="text-[#D4A63D] font-bold">{tooltip.name}</div>
            <div className="text-[#F8F8F4]/90">{tooltip.count} instituți{tooltip.count === 1 ? "e" : "i"}</div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-3 flex-wrap text-[10px] text-[#A0AABF]">
        <span className="tracking-widest uppercase font-bold">Densitate:</span>
        {[
          { c: "#1B2543", l: "0" },
          { c: "#3A2E1E", l: "1-2" },
          { c: "#6A4E28", l: "3-4" },
          { c: "#A0762E", l: "5-6" },
          { c: "#C8952E", l: "7-8" },
          { c: "#D4A63D", l: "9+" },
        ].map((s) => (
          <span key={s.l} className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: s.c }} /> {s.l}
          </span>
        ))}
      </div>
    </div>
  );
};

export default RomaniaMap;
